import React, { ComponentProps } from 'react'
import { useTranslation } from 'hooks/useTranslation'
import {
  FeedBetterRoot,
  HeaderBox,
  Header,
  SubHeader,
  RecipesBox,
  RecipeIcons,
  RecipesColumn,
  BodyBox,
  ImageColumn,
  Title,
  Description,
  IconWrapper,
  ImageWrapper,
  SeeMoreButton,
  IconImage,
  CtaButtonBox,
} from './FeedBetter.styles'
import { Button } from 'components/Button'
import Image, { StaticImageData } from 'next/image'
import freezeDried from '../../../../../../../public/assets/home/freeze-dried-ingredient-preparation.webp'
import frozenMeat from '../../../../../../../public/assets/product-v2/frozen-meat.webp'
import beef from '../../../../../../../public/assets/icons/brand-refresh/beef.webp'
import lamb from '../../../../../../../public/assets/icons/brand-refresh/lamb.webp'
import turkey from '../../../../../../../public/assets/icons/brand-refresh/turkey.webp'
import chicken from '../../../../../../../public/assets/icons/brand-refresh/chicken.webp'
import duck from '../../../../../../../public/assets/icons/brand-refresh/duck.webp'
import { TranslationKeys } from 'locales/translationKeys'
import Link from 'next/link'
import { Text } from 'components/Text'

type FeedBetterOwnProps = ComponentProps<typeof FeedBetterRoot>
export type FeedBetterProps = FeedBetterOwnProps & {}

export const FeedBetter = (_props: FeedBetterProps) => {
  const { t } = useTranslation()

  type protein = {
    icon: StaticImageData
    alt: TranslationKeys
    link: string
  }

  const frozenProteins: protein[] = [
    { icon: beef, alt: 'products-v2:all-products.recipes.frozen.meat-options.beef', link: '/recipes/beef' },
    {
      icon: turkey,
      alt: 'products-v2:all-products.recipes.frozen.meat-options.turkey',
      link: '/recipes/turkey',
    },
    {
      icon: chicken,
      alt: 'products-v2:all-products.recipes.frozen.meat-options.chicken',
      link: '/recipes/chicken',
    },
    { icon: duck, alt: 'products-v2:all-products.recipes.frozen.meat-options.duck', link: '/recipes/duck' },
    { icon: lamb, alt: 'products-v2:all-products.recipes.frozen.meat-options.lamb', link: '/recipes/lamb' },
  ]

  const freezeDriedProteins: protein[] = [
    {
      icon: beef,
      alt: 'products-v2:all-products.recipes.freeze-dried.meat-options.beef',
      link: '/recipes/freeze-dried-raw-beef',
    },
    {
      icon: chicken,
      alt: 'products-v2:all-products.recipes.freeze-dried.meat-options.chicken',
      link: '/recipes/freeze-dried-raw-chicken',
    },
  ]

  type IconProps = {
    src: StaticImageData
    text: TranslationKeys
    link: string
  }

  const Icon = ({ src, text, link }: IconProps) => (
    <Link passHref href={link}>
      <IconWrapper>
        <IconImage>
          <Image src={src} alt={t(text)} />
        </IconImage>
        <Text size="h5" as="p" transform="capitalize" font="label">
          {t(text)}
        </Text>
      </IconWrapper>
    </Link>
  )

  return (
    <FeedBetterRoot>
      <HeaderBox>
        <Header>{t('products-v2:all-products.recipes.headline')}</Header>
        <SubHeader>{t('products-v2:all-products.recipes.subheadline')}</SubHeader>
      </HeaderBox>
      <RecipesBox>
        <ImageColumn>
          <ImageWrapper>
            <Image
              layout="fill"
              objectFit="cover"
              src={frozenMeat}
              alt={t('products-v2:all-products.recipes.frozen.image')}
            />
          </ImageWrapper>
        </ImageColumn>
        <RecipesColumn>
          <BodyBox>
            <Title>{t('products-v2:all-products.recipes.frozen.section-title')}</Title>
            <Description>{t('products-v2:all-products.recipes.frozen.description')}</Description>
          </BodyBox>
          <RecipeIcons>
            {frozenProteins.map((meat, index) => (
              <Icon key={`frozen-${index}`} src={meat.icon} text={meat.alt} link={meat.link} />
            ))}
          </RecipeIcons>
          <Link passHref href="/recipes">
            <SeeMoreButton variant="secondaryAlt">
              {t('products-v2:all-products.recipes.frozen.cta-button')}
            </SeeMoreButton>
          </Link>
        </RecipesColumn>
      </RecipesBox>
      <RecipesBox className="freeze">
        <RecipesColumn className="freeze">
          <BodyBox>
            <Title>{t('products-v2:all-products.recipes.freeze-dried.section-title')}</Title>
            <Description>{t('products-v2:all-products.recipes.freeze-dried.description')}</Description>
          </BodyBox>
          <RecipeIcons>
            {freezeDriedProteins.map((meat, index) => (
              <Icon
                key={`freeze-dried-${index}`}
                src={meat.icon}
                text={meat.alt}
                link={meat.link}
              />
            ))}
          </RecipeIcons>
          <Link passHref href="/recipes/freeze-dried-raw">
            <SeeMoreButton className="freeze" variant="secondaryAlt">
              {t('products-v2:all-products.recipes.freeze-dried.cta-button')}
            </SeeMoreButton>
          </Link>
        </RecipesColumn>
        <ImageColumn className="freeze">
          <ImageWrapper>
            <Image
              layout="fill"
              objectFit="cover"
              src={freezeDried}
              alt={t('home:proteins.image-v2')}
            />
          </ImageWrapper>
        </ImageColumn>
      </RecipesBox>
      <CtaButtonBox>
        <Link href="/meal-plan" passHref>
          <Button>{t('products-v2:all-products.cta')}</Button>
        </Link>
      </CtaButtonBox>
    </FeedBetterRoot>
  )
}

FeedBetter.displayName = 'FeedBetter'
